import type { MessageResponse } from '@/domain/Api';
import type { CartPeriod } from '@/domain/Cart';
import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';
import type { CartResponse } from './cartQueries';

export interface CreateCartRequest {
  name?: string;
  items?: Array<{
    price_id: number;
    quantity: number;
  }>;
}

export interface AddItemToCartRequest {
  price_id: number;
  quantity: number;
}

export interface DeleteCartItemRequest {
  cartItemId: number;
}

export interface UpdateCartPeriodRequest {
  period: CartPeriod;
}

export interface ValidatePromoCodeRequest {
  promo_code: string;
}

export const createCartMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.create(),
    mutationFn: async (values: CreateCartRequest) =>
      api
        .post('carts', {
          json: values,
        })
        .json<CartResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carts.all() });
      toast.success('Cart created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create cart');
    },
  });
};

export const addItemToCartMutation = (cartId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.addItemToCart(cartId),
    mutationFn: async (values: AddItemToCartRequest) =>
      api
        .post(`carts/${cartId}/items`, {
          json: values,
        })
        .json<CartResponse>(),
    onError: (error) => {
      toast.error(error.message || 'Failed to add item to cart');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.carts.cart(cartId),
      });
    },
  });
};

export const deleteCartItemMutation = (cartId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.deleteCartItem(cartId),
    mutationFn: async (values: DeleteCartItemRequest) =>
      api
        .delete(`carts/${cartId}/items/${values.cartItemId}`)
        .json<MessageResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.carts.cart(cartId),
      });
      toast.success('Item removed from cart');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove item from cart');
    },
  });
};

export const deleteCartMutation = (cartId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.delete(cartId),
    mutationFn: async () =>
      api.delete(`carts/${cartId}`).json<MessageResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carts.all() });
      toast.success('Cart deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete cart');
    },
  });
};

export const updateCartPeriodMutation = (cartId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.updatePeriod(cartId),
    mutationFn: async (values: UpdateCartPeriodRequest) =>
      api
        .put(`carts/${cartId}/period`, {
          json: values,
        })
        .json<CartResponse>(),
    onError: (error) => {
      toast.error(error.message || 'Failed to update cart period');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.carts.cart(cartId),
      });
    },
  });
};

export const validatePromoCodeMutation = (cartId: number) => {
  return mutationOptions({
    mutationKey: mutationKeys.carts.validatePromoCode(cartId),
    mutationFn: async (values: ValidatePromoCodeRequest) =>
      api
        .post(`carts/${cartId}/apply-promo`, {
          json: values,
        })
        .json<CartResponse>(),
    onError: (error) => {
      toast.error(error.message || 'Invalid promo code');
    },
    onSuccess: () => {
      toast.success('Promo code applied successfully');
    },
  });
};

export const cartDoneMutation = (cartId: number) => {
  return mutationOptions({
    mutationKey: mutationKeys.carts.done(cartId),
    mutationFn: async () =>
      api.put(`admin/patient-carts/${cartId}/done`).json<MessageResponse>(),
    onError: (error) => {
      toast.error(error.message || 'Failed to send cart verification email');
    },
  });
};

export const buyAgainCartMutation = (cartId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.carts.buyAgain(cartId),
    mutationFn: async () => {
      const res = await api
        .post(`carts/${cartId}/buy-again`)
        .json<CartResponse>();
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carts.all() });
      toast.success('Cart duplicated. You can review and checkout.');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to duplicate cart');
    },
  });
};
